export const DATE_FORMATS = {
  short: { month: 'short', day: 'numeric' },
  medium: { month: 'short', day: 'numeric', year: 'numeric' },
  long: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' },
  full: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
  fullDateTime: { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' },
} as const;

type DateFormatType = keyof typeof DATE_FORMATS;

const ISO_DATE_WITHOUT_TIMEZONE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;

function normalizeDateString(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  if (ISO_DATE_WITHOUT_TIMEZONE.test(trimmed)) {
    return `${trimmed.replace(' ', 'T')}Z`;
  }

  return trimmed;
}

export function parseDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const date = new Date(normalizeDateString(value));
  return isNaN(date.getTime()) ? null : date;
}

export function formatDate(date: Date | string, format: DateFormatType = 'short'): string {
  const d = parseDate(date);
  if (!d) return '';

  return new Intl.DateTimeFormat('en-US', DATE_FORMATS[format]).format(d);
}

export function isValidDate(date: unknown): boolean {
  return date instanceof Date && !isNaN(date.getTime());
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function getDaysDiff(date1: Date, date2: Date): number {
  const diffTime = Math.abs(date2.getTime() - date1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function isToday(date: Date | string): boolean {
  const d = parseDate(date);
  if (!d) return false;
  const today = new Date();
  return d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();
}

export function isYesterday(date: Date | string): boolean {
  const d = parseDate(date);
  if (!d) return false;
  const yesterday = addDays(new Date(), -1);
  return d.getDate() === yesterday.getDate() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getFullYear() === yesterday.getFullYear();
}

export function formatRelativeDate(date: Date | string): string {
  const d = parseDate(date);
  if (!d) return '';

  if (isToday(d)) return 'Today';
  if (isYesterday(d)) return 'Yesterday';

  const daysDiff = getDaysDiff(d, new Date());
  if (daysDiff < 7) return `${daysDiff} days ago`;
  if (daysDiff < 30) return `${Math.floor(daysDiff / 7)} weeks ago`;
  if (daysDiff < 365) return `${Math.floor(daysDiff / 30)} months ago`;
  return `${Math.floor(daysDiff / 365)} years ago`;
}


export function formatRelativeTime(date: Date | string | null | undefined, labels?: { ago?: string; never?: string }): string {
  if (!date) return labels?.never ?? 'Never synced';

  const d = parseDate(date);
  if (!d) return labels?.never ?? 'Never synced';

  const diffMs = Date.now() - d.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const ago = labels?.ago ?? '';

  if (diffMinutes < 60) return `Synced ${diffMinutes}m ${ago}`;
  if (diffHours < 24) return `Synced ${diffHours}h ${ago}`;
  if (diffDays === 1) return 'Synced yesterday';
  return `Synced ${diffDays}d ${ago}`;
}

export function formatTimeAgo(date: Date | string | null | undefined, now: Date = new Date()): string {
  const d = parseDate(date);
  if (!d) return '';

  const diffMs = Math.max(0, now.getTime() - d.getTime());
  const diffSeconds = Math.floor(diffMs / 1000);

  if (diffSeconds < 5) {
    return 'Just now';
  }

  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'always' });

  if (diffSeconds < 60) {
    return formatter.format(-diffSeconds, 'second');
  }

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return formatter.format(-diffMinutes, 'minute');
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return formatter.format(-diffHours, 'hour');
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return formatter.format(-diffDays, 'day');
  }

  if (diffDays < 30) {
    return formatter.format(-Math.floor(diffDays / 7), 'week');
  }

  if (diffDays < 365) {
    return formatter.format(-Math.floor(diffDays / 30), 'month');
  }

  return formatter.format(-Math.floor(diffDays / 365), 'year');
}
